import { Link } from "react-router-dom";
import { Lock } from "lucide-react";

const TYPE_LABEL = {
  article: "Article",
  briefing: "Briefing",
  whitepaper: "Whitepaper",
  "case-study": "Case Study",
  "research-note": "Research Note",
  outlook: "Outlook",
  perspective: "Perspective",
};

export function ArticleCard({ article, large = false }) {
  if (!article) return null;
  return (
    <Link
      to={`/intelligence/${article.slug}`}
      className={`group block bg-white border border-[var(--border-soft)] hover-lift ${large ? "" : ""}`}
      data-testid={`article-card-${article.slug}`}
    >
      {article.hero_image && (
        <div className={large ? "aspect-[16/9] overflow-hidden" : "aspect-[4/3] overflow-hidden"}>
          <img
            src={article.hero_image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="eyebrow">{TYPE_LABEL[article.type] || "Article"}</div>
          {article.premium_only && (
            <div className="eyebrow text-[var(--brass)] flex items-center gap-1">
              <Lock size={10} /> Premium
            </div>
          )}
        </div>
        <h3
          className={`font-display font-bold mt-3 text-[var(--ink)] group-hover:text-[var(--oxblood)] transition-colors ${
            large ? "text-3xl leading-tight" : "text-xl leading-snug"
          }`}
        >
          {article.title}
        </h3>
        {article.dek && <p className="mt-2 text-[15px] leading-relaxed text-[var(--ink-soft)]">{article.dek}</p>}
        <div className="mt-4 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--ink-soft)] flex items-center gap-3">
          {(article.industry_slugs || []).slice(0, 2).map((s) => (
            <span key={s} className="text-[var(--oxblood)]">{s.replace(/-/g, " ")}</span>
          ))}
          <span>·</span>
          <span>{article.read_minutes || 5} min read</span>
        </div>
      </div>
    </Link>
  );
}

export function IndustryCard({ industry }) {
  return (
    <Link
      to={`/industries/${industry.slug}`}
      className="group block bg-white border border-[var(--border-soft)] hover-lift overflow-hidden"
      data-testid={`industry-card-${industry.slug}`}
    >
      <div className="aspect-[16/10] overflow-hidden bg-[var(--ink)]">
        {industry.hero_image && (
          <img
            src={industry.hero_image}
            alt={industry.name}
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500"
          />
        )}
      </div>
      <div className="p-5">
        <div className="eyebrow eyebrow-muted">
          Industry · {String(industry.order || 0).padStart(2, "0")}
        </div>
        <h3 className="font-display font-bold text-2xl mt-2 text-[var(--ink)] group-hover:text-[var(--oxblood)] transition-colors">
          {industry.name}
        </h3>
        {industry.tagline && (
          <p className="mt-2 text-sm text-[var(--ink-soft)] leading-snug">{industry.tagline}</p>
        )}
        {(industry.market_size_usd_b || industry.cagr_pct) ? (
          <div className="mt-4 flex gap-6 text-xs font-mono uppercase tracking-wider">
            {industry.market_size_usd_b ? (
              <div>
                <div className="text-[var(--ink-soft)]">MKT</div>
                <div className="text-[var(--ink)] text-base">${industry.market_size_usd_b}B</div>
              </div>
            ) : null}
            {industry.cagr_pct ? (
              <div>
                <div className="text-[var(--ink-soft)]">CAGR</div>
                <div className="text-[var(--oxblood)] text-base">{industry.cagr_pct}%</div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Link>
  );
}

export function ReportCard({ report }) {
  return (
    <Link
      to={`/research/${report.slug}`}
      className="group block bg-white border border-[var(--border-soft)] hover-lift overflow-hidden"
      data-testid={`report-card-${report.slug}`}
    >
      {report.cover_image && (
        <div className="aspect-[3/2] overflow-hidden">
          <img
            src={report.cover_image}
            alt={report.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        </div>
      )}
      <div className="p-5">
        <div className="eyebrow">Research Report · {report.pages || 0} pp.</div>
        <h3 className="font-display font-bold text-xl mt-2 text-[var(--ink)] group-hover:text-[var(--oxblood)] transition-colors leading-snug">
          {report.title}
        </h3>
        <p className="mt-2 text-sm text-[var(--ink-soft)] line-clamp-2">{report.summary}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="font-mono text-[var(--ink)] text-lg">${(report.price_usd || 0).toLocaleString()}</span>
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--oxblood)]">Preview →</span>
        </div>
      </div>
    </Link>
  );
}

export function CompanyCard({ company }) {
  return (
    <Link
      to={`/companies/${company.slug}`}
      className="group block bg-white border border-[var(--border-soft)] p-5 hover-lift"
      data-testid={`company-card-${company.slug}`}
    >
      <div className="flex items-center gap-3">
        {company.logo_url ? (
          <img src={company.logo_url} alt={company.name} className="w-10 h-10 object-contain" />
        ) : (
          <div className="w-10 h-10 bg-[var(--ink)] text-[var(--parchment)] flex items-center justify-center font-display font-bold">
            {company.name?.[0]}
          </div>
        )}
        <div>
          <div className="font-display font-bold text-lg text-[var(--ink)] group-hover:text-[var(--oxblood)]">{company.name}</div>
          {company.ticker && <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--ink-soft)]">{company.ticker}</div>}
        </div>
      </div>
      <div className="mt-4 flex gap-4 text-[11px] font-mono uppercase">
        {company.market_cap_usd_b ? (
          <div><div className="text-[var(--ink-soft)]">MKT CAP</div><div className="text-[var(--ink)]">${company.market_cap_usd_b}B</div></div>
        ) : null}
        {company.revenue_usd_b ? (
          <div><div className="text-[var(--ink-soft)]">REV</div><div className="text-[var(--ink)]">${company.revenue_usd_b}B</div></div>
        ) : null}
      </div>
    </Link>
  );
}

export function PodcastCard({ podcast }) {
  return (
    <Link
      to={`/podcasts/${podcast.slug}`}
      className="group block bg-white border border-[var(--border-soft)] overflow-hidden hover-lift"
      data-testid={`podcast-card-${podcast.slug}`}
    >
      {podcast.cover_art && (
        <div className="aspect-square overflow-hidden">
          <img src={podcast.cover_art} alt={podcast.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
        </div>
      )}
      <div className="p-5">
        <div className="eyebrow">Episode {String(podcast.episode_number || 0).padStart(2, "0")} · S{podcast.season || 1}</div>
        <h3 className="font-display font-bold text-lg mt-2 text-[var(--ink)] group-hover:text-[var(--oxblood)] leading-snug">{podcast.title}</h3>
        {podcast.duration_seconds ? (
          <div className="mt-3 text-[11px] font-mono uppercase text-[var(--ink-soft)]">
            {Math.round(podcast.duration_seconds / 60)} min
          </div>
        ) : null}
      </div>
    </Link>
  );
}
